import { useEffect, useRef, useState } from "react";

// Wave 3 C6 — one shared EventSource per tab for /api/scheduling/events.
// Components call useRealtimeEvents({ "booking.status": handler }) and the
// stream is opened lazily / closed when the last subscriber unmounts.
const BASE = process.env.REACT_APP_BACKEND_URL || "";
const listeners = new Map(); // type -> Set<fn>
let source = null;
let refCount = 0;
let statusListeners = new Set();
let status = "idle"; // idle | connecting | open | error

function setStatus(next) {
  status = next;
  statusListeners.forEach((fn) => fn(next));
}

function dispatch(type, evt) {
  let payload;
  try { payload = JSON.parse(evt.data); } catch { return; }
  listeners.get(type)?.forEach((fn) => { try { fn(payload); } catch { /* listener error */ } });
  listeners.get("*")?.forEach((fn) => { try { fn(payload); } catch { /* listener error */ } });
}

const EVENT_TYPES = ["booking.status", "notification", "booking.assigned", "driver.message"];

function open() {
  const token = localStorage.getItem("auth_token");
  if (!token || source || typeof EventSource === "undefined") return;
  setStatus("connecting");
  source = new EventSource(`${BASE}/api/scheduling/events?token=${encodeURIComponent(token)}`);
  source.onopen = () => setStatus("open");
  source.onerror = () => setStatus("error"); // EventSource auto-reconnects (server sends retry: 3000)
  EVENT_TYPES.forEach((t) => source.addEventListener(t, (e) => dispatch(t, e)));
}

function close() {
  if (source) { source.close(); source = null; }
  setStatus("idle");
}

export function useRealtimeEvents(handlers, { enabled = true } = {}) {
  const ref = useRef(handlers);
  ref.current = handlers;
  const [connState, setConnState] = useState(status);

  useEffect(() => {
    if (!enabled) return undefined;
    const bound = Object.keys(ref.current).map((type) => {
      const fn = (payload) => ref.current[type]?.(payload);
      if (!listeners.has(type)) listeners.set(type, new Set());
      listeners.get(type).add(fn);
      return [type, fn];
    });
    statusListeners.add(setConnState);
    refCount += 1;
    open();
    return () => {
      bound.forEach(([type, fn]) => listeners.get(type)?.delete(fn));
      statusListeners.delete(setConnState);
      refCount -= 1;
      if (refCount <= 0) { refCount = 0; close(); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return connState;
}

// Re-open after login/logout token changes.
export function resetRealtimeConnection() {
  close();
  if (refCount > 0) open();
}
